import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { Button, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLoaderData, useMatches } from '@remix-run/react';
import { useState } from 'react';
import Image from 'remix-image';
import { MuiRemixLink } from '~/components/shared/links';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { Product, ProductPrice } from '~/services/api.server';
import { isPackProduct } from '~/utils/common';
import DesignStatusChip from './DesignStatusChip';
import QuickShopModal from './QuickShopModal';

const FractionOrPriceSymbol: React.FC = ({ children }) => (
  <Typography component="span" variant="body2" position="relative" top="-0.5em">
    {children}
  </Typography>
);

type FormatPriceProps = {
  prices: ProductPrice;
  isInPack?: boolean;
};

function FormatPrice({ prices, isInPack }: FormatPriceProps) {
  const [whole, fraction] = prices.price.display.split('.');

  return (
    <Typography component="span" variant="h5" fontWeight="medium" mx="1px" color={isInPack ? 'primary.main' : ''}>
      <FractionOrPriceSymbol>$</FractionOrPriceSymbol>
      {whole}
      <FractionOrPriceSymbol>{fraction}</FractionOrPriceSymbol>
    </Typography>
  );
}

function ProductImage({ product }: { product: Product }) {
  return product?.image || product?.blank_image ? (
    <Image
      src={product?.image || product?.blank_image}
      alt={product.name}
      responsive={[{ size: { width: 300, height: 300 } }]}
      options={{ fit: 'inside' }}
      dprVariants={[1, 3]}
      loading="lazy"
      style={{
        display: 'block',
        backgroundRepeat: 'no-repeat',
        minWidth: 'auto',
        minHeight: 'auto',
        width: '100%',
        aspectRatio: '1 / 1',
        marginBottom: 16,
      }}
    />
  ) : (
    <img src={NoImageAvailable} alt="no available" style={{ width: '100%', minWidth: 'auto', minHeight: 'auto' }} />
  );
}

type ProductCardProps = {
  product: Product;
  endIcon?: React.ReactNode;
  onProductImageClick?: () => void;
  isInPack?: boolean;
  isDashboard?: boolean;
};
export default function ProductCard({
  product,
  endIcon,
  onProductImageClick,
  isInPack,
  isDashboard,
}: ProductCardProps) {
  const href = `/shop/${product.slug}`;
  const theme = useTheme();
  const isShop = useMatches().some((match) => match.id.includes('shop') && !match.id.includes('$productSlug'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const data = useLoaderData();

  const [openModal, setOpenModal] = useState(false);

  const renderQuickShopButton = [!isPackProduct(product), isShop || isDashboard, !isMobile].every(Boolean);

  return (
    <Card
      component={Stack}
      elevation={0}
      sx={{
        position: 'relative',
        borderRadius: 2,
        border: '#EBEBEB 1px solid',
        '&:hover': { border: '#009DC8 1px solid' },
        '& .MuiCardActionArea-focusHighlight': { backgroundColor: '#fff' },
        outline: isInPack ? `1px solid ${theme.palette.primary.main}` : null,
        backgroundColor: isInPack ? '#E4EBF3' : '',
      }}
    >
      {onProductImageClick ? (
        <CardActionArea sx={{ lineHeight: 0, bgcolor: '#F8FAFC' }}>
          <DesignStatusChip status={product.design_status?.name} borderRadius="8px 0 4px" />
          <Box mx={1} onClick={() => onProductImageClick()}>
            <ProductImage product={product} />
          </Box>
        </CardActionArea>
      ) : (
        <CardActionArea component={MuiRemixLink} href={href} sx={{ lineHeight: 0, bgcolor: '#F8FAFC' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 1 }}>
            <DesignStatusChip status={product.design_status?.name} borderRadius="8px 0 4px" />
            {product.safsira_connected_product && (
              <Tooltip title="Powered with Safsira Design Lab" placement="top" arrow>
                <BoltOutlinedIcon sx={{ color: 'primary.main', fontSize: 30, '&:hover': { color: '#24598A' } }} />
              </Tooltip>
            )}
          </Box>
          <Box mx={1}>
            <ProductImage product={product} />
          </Box>
        </CardActionArea>
      )}
      <CardContent component={Stack} justifyContent="space-between" sx={{ gap: 0.5, flex: 1 }}>
        <Typography
          component="h2"
          variant="subtitle2"
          gutterBottom
          sx={{
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            flex: 1,
          }}
        >
          {onProductImageClick ? (
            <Box
              color={isInPack ? 'primary.main' : 'inherit'}
              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
              onClick={() => onProductImageClick()}
            >
              {product.name}
            </Box>
          ) : (
            <MuiRemixLink href={href} color="inherit" underline="none" sx={{ '&:hover': { color: 'primary.main' } }}>
              {product.name}
            </MuiRemixLink>
          )}
        </Typography>
        <Stack direction="row" justifyContent={product.prices ? 'space-between' : 'end'} alignItems="center">
          {product.prices && <FormatPrice prices={product.prices} isInPack={isInPack} />}
          {renderQuickShopButton && (
            <Button variant="outlined" sx={{ textTransform: 'uppercase', px: 1 }} onClick={() => setOpenModal(true)}>
              Quick shop
            </Button>
          )}
        </Stack>
        <Box position="absolute" right={10} bottom={10}>
          {endIcon}
        </Box>
      </CardContent>
      {openModal && (
        <QuickShopModal product={product} isAuthenticated={data.isAuthenticated} onClose={() => setOpenModal(false)} />
      )}
    </Card>
  );
}
